import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import type { ChatMessage, SendMessageArgs, UserMessage } from '../../hooks/use-chat-ws';
import AskUserQuestionFlow, { type AskUserQuestionInput } from '../ask-user-question-flow';
import PinnedTodoList, { type TodoItemData } from '../pinned-todo-list';
type PinnedStackProps = {
  todos: TodoItemData[];
  /** Whether the plan is still worth showing — the caller already tracks this. */
  showTodos: boolean;
  /** The active unanswered AskUserQuestion tool_use, if one is pinned. */
  question?: ChatMessage;
  /** The live browser row, while the browser pane is not showing. */
  browser?: ReactNode;
  answersByQuestionId: Record<string, UserMessage>;
  onSendMessage: (content: string, args: SendMessageArgs) => void;
  /** Running-agents indicator, grouped inside the plan card as its footer. Set only when agents
   *  are present and the plan is pinned; otherwise the caller shows the standalone pill. */
  agentsFooter?: ReactNode;
};

/**
 * The scrollport geometry the stack depends on, owned here so chat-panel and the stories
 * can't drift apart. Three coupled pieces:
 *
 * - the scroll container must be a size container, so `100cqh` means "the scrollport";
 * - the scrollback declares `--pinned-top-inset` next to its top padding, because a sticky
 *   box is confined to its containing block's *content* box — at a full `100cqh` the stack
 *   can't rise past that padding and ends up clamped below the composer;
 * - `-mx-1` cancels the scrollback's wider `px-4` gutter so the cards line up with the
 *   composer, which sits in a `px-3` band below.
 */
// overflow-x-clip: setting overflow-y computes the unset axis to auto, so a single pixel of wide
// content grew a horizontal overlay scrollbar on the scrollport's bottom edge — right over the row
// beneath it. Wide content scrolls inside its own container, never the transcript.
export const PINNED_SCROLLPORT_CLASSNAME = 'flex-1 overflow-y-auto overflow-x-clip @container-[size]';
// A phone's scrollport is short (shorter still with the keyboard up), so it keeps a small top pad.
export const PINNED_SCROLLBACK_CLASSNAME = 'px-4 pt-6 [--pinned-top-inset:1.5rem] md:pt-20 md:[--pinned-top-inset:5rem]';
export const PINNED_STACK_CLASSNAME = 'sticky bottom-0 z-10 mt-auto -mx-1 flex flex-col gap-2 max-h-[calc(100cqh-var(--pinned-top-inset,0px))] pt-3 pb-2';

/** Whether the stack would render anything. Callers need this to lay out around it. */
export const hasPinnedContent = ({
  showTodos,
  question,
  browser
}: Pick<PinnedStackProps, 'showTodos' | 'question' | 'browser'>): boolean => showTodos || question?.type === 'tool_use' || !!browser;

/**
 * The cards that hold the bottom of the conversation: the current plan and the active question.
 * Rendered as the scrollback's last child and stuck to the viewport bottom, so earlier messages
 * pass behind them — expanding a card overlays the conversation instead of reflowing it, and at the
 * bottom of the scroll the stack takes real space so no message hides underneath.
 *
 * Depends on the scroll container being a size container (`100cqh` bounds the stack, so an
 * expanded card grows with its content and stops at the panel) whose content fills it
 * (`mt-auto` bottom-anchors the stack before the conversation is long enough to scroll).
 * See chat-panel.
 */
const PinnedStack = t0 => {
  const $ = _c(14);
  const {
    todos,
    showTodos,
    question,
    browser,
    answersByQuestionId,
    onSendMessage,
    agentsFooter
  } = t0;
  if (!hasPinnedContent({
    showTodos,
    question,
    browser
  })) {
    return null;
  }
  let t1;
  if ($[0] !== agentsFooter || $[1] !== browser || $[2] !== question || $[3] !== showTodos || $[4] !== todos) {
    t1 = showTodos && <PinnedTodoList todos={todos} variant="pinned" startCollapsed={!!question || !!browser} footer={agentsFooter} />;
    $[0] = agentsFooter;
    $[1] = browser;
    $[2] = question;
    $[3] = showTodos;
    $[4] = todos;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== answersByQuestionId || $[7] !== onSendMessage || $[8] !== question) {
    t2 = question?.type === "tool_use" && <AskUserQuestionFlow key={question.id} questions={question.input.questions as AskUserQuestionInput[] ?? []} onSendMessage={onSendMessage} questionId={question.id} questionAnswerMessages={answersByQuestionId} disabled={false} />;
    $[6] = answersByQuestionId;
    $[7] = onSendMessage;
    $[8] = question;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== browser || $[11] !== t1 || $[12] !== t2) {
    t3 = <div className={PINNED_STACK_CLASSNAME}>{browser}{t1}{t2}</div>;
    $[10] = browser;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
export default PinnedStack;